<template>
  <div class="slider" :class="sliderClasses">
    <div v-if="showLabels" class="slider__labels">
      <span>{{ actualMinLabel }}</span>
      <span>{{ actualMaxLabel }}</span>
    </div>

    <div
      class="slider__track"
      ref="track"
      @click="handleTrackClick"
      :style="trackStyle"
    >
      <div class="slider__limit" :style="limitStyle" />
      <div class="slider__progress" :style="progressStyle" />

      <div v-if="showSteps" class="slider__steps">
        <span
          v-for="(step, index) in stepsArray"
          :key="index"
          class="slider__step"
          :style="{ left: `${step}%` }"
        />
      </div>

      <div
        class="slider__thumb"
        :style="thumbStyle"
        @mousedown="startDrag"
        @touchstart="startDrag"
      >
        <div
          v-if="showTooltip"
          class="slider__tooltip"
          :class="tooltipClasses"
          :style="tooltipStyle"
          ref="tooltip"
        >
          {{ displayValue }}
        </div>
      </div>
    </div>

    <div v-if="showValue" class="slider__value">
      {{ displayValue }}
    </div>
  </div>
</template>

<script>
export default {
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: [Number, String],
      default: 0,
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 100,
    },
    step: {
      type: Number,
      default: 1,
      validator: v => v > 0
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    limitThreshold: {
      type: Number,
      default: null,
    },
    showLabels: {
      type: Boolean,
      default: false,
    },
    showValue: {
      type: Boolean,
      default: false,
    },
    showTooltip: {
      type: Boolean,
      default: false,
    },
    showSteps: {
      type: Boolean,
      default: false,
    },
    height: {
      type: String,
      default: '8px',
    },
    thumbSize: {
      type: String,
      default: '24px',
    },
    trackColor: {
      type: String,
      default: 'rgba(251, 146, 60, 0.3)',
    },
    progressColor: {
      type: String,
      default: '#FB923C',
    },
    thumbColor: {
      type: String,
      default: '#FB923C',
    },
    valueFormat: {
      type: Function,
      default: v => v.toString(),
    },
    minLabel: {
      type: String,
      default: null,
    },
    maxLabel: {
      type: String,
      default: null,
    },
    tooltipPosition: {
      type: String,
      default: 'top',
      validator: (value) => ['top', 'bottom'].includes(value),
    },
    ceilIntermediate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentValue: this.clamp(Number(this.value)),
      isDragging: false,
      startX: 0,
      startValue: 0,
      isTouchDevice: false,
      tooltipDimensions: { width: 0, height: 0 },
    };
  },
  computed: {
    effectiveMin() {
      if (this.limitThreshold !== null) {
        return Math.max(this.min, this.limitThreshold);
      }
      return this.min;
    },
    displayValue() {
      try {
        return this.valueFormat(this.currentValue);
      } catch (_) {
        return this.currentValue.toString();
      }
    },
    percentage() {
      const range = this.max - this.min;
      if (range === 0) return 0;
      return ((this.currentValue - this.min) / range) * 100;
    },
    limitPercentage() {
      if (this.limitThreshold === null) return 0;
      const range = this.max - this.min;
      if (range === 0) return 0;
      const pct = ((this.limitThreshold - this.min) / range) * 100;
      return Math.max(0, Math.min(100, pct));
    },
    stepsArray() {
      if (!this.showSteps) return [];
      const arr = [];
      const count = (this.max - this.min) / this.step;
      for (let i = 0; i <= count; i++) {
        arr.push((i / count) * 100);
      }
      return arr;
    },
    trackStyle() {
      return {
        height: this.height,
        backgroundColor: this.trackColor,
        borderRadius: this.height,
      };
    },
    limitStyle() {
      if (this.limitThreshold === null) return { display: 'none' };
      return {
        width: `${this.limitPercentage}%`,
        backgroundColor: '#FF3B30',
        height: this.height,
        borderRadius: this.height,
        position: 'absolute',
        left: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 3,
      };
    },
    progressStyle() {
      return {
        width: `${this.percentage}%`,
        backgroundColor: this.progressColor,
        height: this.height,
        borderRadius: this.height,
      };
    },
    thumbStyle() {
      return {
        left: `${this.percentage}%`,
        width: this.thumbSize,
        height: this.thumbSize,
        backgroundColor: this.thumbColor,
        transform: 'translate(-50%, -50%)',
      };
    },
    tooltipStyle() {
      if (!this.showTooltip) return { display: 'none' };

      const GAP = 8;
      const ARROW_SIZE = 6;

      const style = {
        position: 'absolute',
        opacity: 1,
        visibility: 'visible',
      };

      if (this.tooltipPosition === 'top') {
        style.bottom = `${this.thumbSize / 2 + GAP + ARROW_SIZE}px`;
        style.left = '50%';
        style.transform = 'translateX(-50%)';
      } else {
        style.top = `${this.thumbSize / 2 + GAP + ARROW_SIZE}px`;
        style.left = '50%';
        style.transform = 'translateX(-50%)';
      }

      return style;
    },
    sliderClasses() {
      return {
        'slider--disabled': this.disabled,
        'slider--dragging': this.isDragging,
      };
    },
    tooltipClasses() {
      return {
        [`slider__tooltip--${this.tooltipPosition}`]: true,
      };
    },
    actualMinLabel() {
      return this.minLabel != null ? this.minLabel : this.min.toString();
    },
    actualMaxLabel() {
      return this.maxLabel != null ? this.maxLabel : this.max.toString();
    },
  },
  methods: {
    clamp(val) {
      const range = this.max - this.min;
      if (range === 0) return this.min;

      const lower = this.limitThreshold !== null
        ? Math.max(this.min, this.limitThreshold)
        : this.min;

      let v = Math.max(lower, Math.min(this.max, val));

      const epsilon = 0.001;
      if (v >= this.max - epsilon) {
        return this.max;
      }
      if (v <= lower + epsilon) {
        return lower;
      }

      if (this.step > 0 && range > 0) {
        const steps = Math.round((v - lower) / this.step);
        v = lower + steps * this.step;

        if (v >= this.max - epsilon) {
          return this.max;
        }
        if (v <= lower + epsilon) {
          return lower;
        }
      }

      if (this.ceilIntermediate) {
        const ceiled = Math.ceil(v);
        if (ceiled >= this.max) return this.max;
        if (ceiled <= lower) return lower;
        return ceiled;
      }

      return v;
    },
    emitValue(v) {
      const clamped = this.clamp(v);
      if (clamped !== this.value) {
        this.$emit('input', clamped);
        this.$emit('change', clamped);
      }
    },
    detectTouchDevice() {
      this.isTouchDevice =
        typeof window !== 'undefined' &&
        ('ontouchstart' in window ||
          navigator.maxTouchPoints > 0 ||
          navigator.msMaxTouchPoints > 0);
    },
    calculateTooltipDimensions() {
      this.$nextTick(() => {
        const tooltip = this.$refs.tooltip;
        if (!tooltip) return;

        const rect = tooltip.getBoundingClientRect();
        this.tooltipDimensions = {
          width: rect.width || 60,
          height: rect.height || 24,
        };
      });
    },
    updateTooltipPosition() {
      if (!this.showTooltip) return;

      this.$nextTick(() => {
        this.calculateTooltipDimensions();
      });
    },
    startDrag(e) {
      if (this.disabled) return;
      this.isDragging = true;
      this.startX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
      this.startValue = this.currentValue;
      e.preventDefault();
      e.stopPropagation();
      document.addEventListener('mousemove', this.onMove);
      document.addEventListener('mouseup', this.onEnd);
      document.addEventListener('touchmove', this.onMove, { passive: false });
      document.addEventListener('touchend', this.onEnd);
    },
    onMove(e) {
      if (!this.isDragging) return;
      e.preventDefault();
      const clientX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
      const rect = this.$refs.track.getBoundingClientRect();

      let percent = ((clientX - rect.left) / rect.width) * 100;

      if (percent <= 0.1) {
        this.currentValue = this.effectiveMin;
        return;
      }
      if (percent >= 99.9) {
        this.currentValue = this.max;
        return;
      }

      percent = Math.max(0, Math.min(100, percent));

      const range = this.max - this.min;
      const val = this.min + (percent / 100) * range;

      this.currentValue = this.clamp(val);
    },
    onEnd() {
      this.isDragging = false;
      this.emitValue(this.currentValue);
      document.removeEventListener('mousemove', this.onMove);
      document.removeEventListener('mouseup', this.onEnd);
      document.removeEventListener('touchmove', this.onMove);
      document.removeEventListener('touchend', this.onEnd);
    },
    handleTrackClick(e) {
      if (this.disabled) return;
      const rect = this.$refs.track.getBoundingClientRect();

      let percent = ((e.clientX - rect.left) / rect.width) * 100;

      if (percent <= 0.1) {
        this.currentValue = this.effectiveMin;
        this.emitValue(this.effectiveMin);
        return;
      }
      if (percent >= 99.9) {
        this.currentValue = this.max;
        this.emitValue(this.max);
        return;
      }

      percent = Math.max(0, Math.min(100, percent));

      const range = this.max - this.min;
      const val = this.min + (percent / 100) * range;

      const clamped = this.clamp(val);
      this.currentValue = clamped;
      this.emitValue(clamped);
    },
    cleanup() {
      window.removeEventListener('resize', this.handleResize);
    },
    handleResize() {
      if (this.showTooltip) {
        this.updateTooltipPosition();
      }
    },
  },
  watch: {
    value(newVal) {
      if (!this.isDragging) {
        this.currentValue = this.clamp(newVal);
      }
    },
    limitThreshold() {
      this.currentValue = this.clamp(this.currentValue);
    },
    showTooltip() {
      if (this.showTooltip) {
        this.updateTooltipPosition();
      }
    },
    min() {
      this.currentValue = this.clamp(this.currentValue);
    },
    max() {
      this.currentValue = this.clamp(this.currentValue);
    },
  },
  mounted() {
    this.detectTouchDevice();
    if (this.showTooltip) {
      this.updateTooltipPosition();
    }
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    this.cleanup();
  },
};
</script>

<style scoped lang="scss">
.slider {
  position: relative;
  width: 100%;
  padding: 20px 0;
  user-select: none;
  touch-action: manipulation;

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }
  &--dragging {
    cursor: grabbing;
  }

  &__labels {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    font-size: 12px;
    color: #666;
  }
  &__track {
    position: relative;
    width: 100%;
    cursor: pointer;
  }
  &__limit {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 3;
  }
  &__progress {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    border-radius: inherit;
    z-index: 2;
  }
  &__steps {
    position: absolute;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 3;
  }
  &__step {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 4px;
    height: 4px;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 50%;
  }
  &__thumb {
    position: absolute;
    top: 50%;
    border-radius: 50%;
    cursor: grab;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: transform;
    z-index: 4;
    &:hover {
      transform: translate(-50%, -50%) scale(1.1);
    }
  }
  &__tooltip {
    position: absolute;
    background: #333;
    color: #fff;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    opacity: 1;
    visibility: visible;
    pointer-events: none;
    z-index: 5;

    &--top {
      bottom: 100%;
      margin-bottom: 8px;

      &::after {
        content: '';
        position: absolute;
        bottom: -6px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 6px 6px 0 6px;
        border-color: #333 transparent transparent transparent;
      }
    }

    &--bottom {
      top: 100%;
      margin-top: 8px;

      &::after {
        content: '';
        position: absolute;
        top: -6px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 0 6px 6px 6px;
        border-color: transparent transparent #333 transparent;
      }
    }
  }
  &__value {
    text-align: center;
    margin-top: 10px;
    font-weight: 500;
    color: #333;
  }
}

.slider--dragging .slider__thumb,
.slider__thumb:active {
  cursor: grabbing;
  transform: translate(-50%, -50%) scale(0.95);
}

@media (max-width: 768px) {
  .slider {
    padding: 15px 0;
    &__labels {
      font-size: 11px;
    }
    &__tooltip {
      font-size: 11px;
      padding: 4px 8px;
    }
  }
}
</style>
